import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiFetch } from '../api/api';
import { formatDisplayValue } from '../utils/formatUtils';
import './WellDetail.css';

// Professional Status Badge Component
const StatusBadge = ({ status, type = 'operational' }) => {
  const getStatusConfig = (status, type) => {
    const configs = {
      operational: {
        'Active': { color: '#28a745', icon: '🟢' },
        'Drilling': { color: '#007bff', icon: '🔵' },
        'Completed': { color: '#6c757d', icon: '⚫' },
        'Suspended': { color: '#ffc107', icon: '🟡' },
        'Abandoned': { color: '#dc3545', icon: '🔴' }
      },
      phase: {
        'Planning': { color: '#6f42c1', icon: '📋' },
        'Drilling': { color: '#007bff', icon: '⚡' },
        'Completion': { color: '#fd7e14', icon: '🔧' },
        'Production': { color: '#28a745', icon: '🛢️' }
      }
    };
    
    return configs[type]?.[status] || { color: '#6c757d', icon: '❓' };
  };

  const config = getStatusConfig(status, type);
  
  return (
    <span className="status-badge" style={{ 
      backgroundColor: config.color + '20', 
      color: config.color,
      border: `1px solid ${config.color}40`
    }}>
      {config.icon} {status}
    </span>
  );
};

// Key Performance Indicator Component
const KPICard = ({ title, value, unit, trend, icon, subtitle, critical = false }) => {
  return (
    <div className={`kpi-card ${critical ? 'critical' : ''}`}>
      <div className="kpi-header">
        <div className="kpi-icon">{icon}</div>
        <div className="kpi-title-group">
          <h4 className="kpi-title">{title}</h4>
          {subtitle && <span className="kpi-subtitle">{subtitle}</span>}
        </div>
        {trend && (
          <div className={`kpi-trend ${trend > 0 ? 'positive' : trend < 0 ? 'negative' : 'neutral'}`}>
            {trend > 0 ? '↗️' : trend < 0 ? '↘️' : '➡️'}
          </div>
        )}
      </div>
      <div className="kpi-value">
        <span className="value">{value}</span>
        {unit && <span className="unit">{unit}</span>}
      </div>
    </div>
  );
};

// Technical Specification Card
const TechSpecCard = ({ title, specs, icon }) => {
  return (
    <div className="tech-spec-card">
      <div className="spec-header">
        <div className="spec-icon">{icon}</div>
        <h3 className="spec-title">{title}</h3>
      </div>
      <div className="spec-content">
        {specs.map((spec, index) => (
          <div key={index} className="spec-item">
            <span className="spec-label">{spec.label}</span>
            <span className="spec-value">{spec.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Module Quick Access Card
const ModuleCard = ({ title, icon, description, count, status, onClick, disabled = false }) => {
  return (
    <div 
      className={`module-card ${disabled ? 'disabled' : ''}`} 
      onClick={!disabled ? onClick : undefined}
    >
      <div className="module-header">
        <div className="module-icon">{icon}</div>
        <div className="module-info">
          <h4 className="module-title">{title}</h4>
          <p className="module-description">{description}</p>
        </div>
        <div className="module-meta">
          <span className="module-count">{count}</span>
          {status && <StatusBadge status={status} type="operational" />}
        </div>
      </div>
      <div className="module-action">
        <span className="action-text">View Details</span>
        <span className="action-arrow">→</span>
      </div>
    </div>
  );
};

export default function WellDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [wells, setWells] = useState([]);
  const [selectedWellId, setSelectedWellId] = useState(id || '');
  const [well, setWell] = useState(null);
  const [plan, setPlan] = useState(null);
  const [dailyReports, setDailyReports] = useState([]);
  const [surveyHeaders, setSurveyHeaders] = useState([]);
  const [surveyPoints, setSurveyPoints] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [bhaData, setBhaData] = useState([]);
  const [bhaComponents, setBhaComponents] = useState([]);
  const [casingRuns, setCasingRuns] = useState([]);
  const [casingTally, setCasingTally] = useState([]);
  const [drillingParameters, setDrillingParameters] = useState([]);
  const [dailyWellData, setDailyWellData] = useState([]);
  const [kpiConfigurations, setKpiConfigurations] = useState([]);
  const [kpiValues, setKpiValues] = useState({});
  const [error, setError] = useState(null);
  
  const [wellTableColumns, setWellTableColumns] = useState([]);
  const [isLoadingConfig, setIsLoadingConfig] = useState(true);
  const [isLoadingData, setIsLoadingData] = useState(true);

  const token = localStorage.getItem('token');
  const role = localStorage.getItem('role');

  // Load wells for dropdown
  const loadWells = useCallback(async () => {
    try {
      const response = await apiFetch('/api/wells');
      const wellsList = Array.isArray(response) ? response : [];
      setWells(wellsList);
      
      // If URL has an ID but selectedWellId is not set, use the URL ID
      if (id && !selectedWellId) {
        setSelectedWellId(id);
      }
    } catch (err) {
      console.error('Failed to load wells:', err);
      setWells([]);
    }
  }, [id, selectedWellId]);

  // Handle well dropdown change
  const handleWellChange = useCallback((e) => {
    const wellId = e.target.value;
    setSelectedWellId(wellId);
    if (wellId) {
      navigate(`/welldetail/${wellId}`);
    }
  }, [navigate]);

  const fetchConfigurations = useCallback(async () => {
    setIsLoadingConfig(true);
    try {
      const allConfigs = await apiFetch('/api/config/columns');
      if (!Array.isArray(allConfigs)) {
        throw new Error("Invalid configuration format from API.");
      }

      const wellConfig = allConfigs.find(config => config.tableName === "Well");
      if (wellConfig && Array.isArray(wellConfig.columns)) {
        setWellTableColumns(wellConfig.columns.sort((a, b) => a.order - b.order));
      } else {
        setWellTableColumns([]);
      }
      
      setError(null);
    } catch (err) {
      console.error('Fetch column config error for WellDetail:', err);
      setError('Failed to load page configuration: ' + (err.message || 'Unknown error'));
      setWellTableColumns([]);
    }
    setIsLoadingConfig(false);
  }, []);

  const loadKpiConfigurations = useCallback(async () => {
    try {
      const response = await apiFetch('/api/config/kpi-configurations');
      const configurations = Array.isArray(response) ? response : [];
      
      console.log('Loaded KPI configurations from backend:', configurations);
      
      // Filter only enabled KPIs and sort by order
      const enabledKpis = configurations
        .filter(kpi => kpi.enabled)
        .sort((a, b) => (a.order || 999) - (b.order || 999));
      
      console.log('Enabled KPIs with format info:', enabledKpis.map(kpi => ({
        id: kpi.id,
        title: kpi.title,
        format: kpi.format,
        unit: kpi.unit,
        tableName: kpi.tableName,
        columnName: kpi.columnName
      })));
      
      setKpiConfigurations(enabledKpis);
    } catch (error) {
      console.error('Failed to load KPI configurations:', error);
      // Use default KPIs if configuration fails
      console.log('Using default KPIs due to error');
      setKpiConfigurations(getDefaultKpis());
    }
  }, []);

  const getDefaultKpis = () => {
    return [
      {
        id: 'current-td',
        title: 'Current TD',
        subtitle: 'Measured Depth',
        icon: '📏',
        tableName: 'DailyWellData',
        columnName: 'ActualTD',
        calculationMethod: 'latest',
        unit: 'm',
        format: 'decimal2',
        critical: true,
        enabled: true,
        order: 1
      },
      {
        id: 'days-spent',
        title: 'Days Spent',
        subtitle: 'Drilling Duration',
        icon: '📅',
        tableName: 'DailyReport',
        columnName: 'ReportDate',
        calculationMethod: 'count',
        unit: 'days',
        format: 'int',
        critical: false,
        enabled: true,
        order: 2
      },
      {
        id: 'total-cost',
        title: 'Total Cost',
        subtitle: 'Cumulative',
        icon: '💰',
        tableName: 'DailyWellData',
        columnName: 'DailyCost',
        calculationMethod: 'sum',
        unit: 'USD',
        format: 'currency',
        critical: true,
        enabled: true,
        order: 3
      },
      {
        id: 'npt-hours',
        title: 'NPT Hours',
        subtitle: 'Non-Productive Time',
        icon: '⏱️',
        tableName: 'DailyWellData',
        columnName: 'NPT_Hours',
        calculationMethod: 'sum',
        unit: 'hrs',
        format: 'decimal2',
        critical: false,
        enabled: true,
        order: 4
      },
      {
        id: 'avg-rop',
        title: 'Avg ROP',
        subtitle: 'Rate of Penetration',
        icon: '⚡',
        tableName: 'DrillingParameters',
        columnName: 'ROP',
        calculationMethod: 'average',
        unit: 'm/hr',
        format: 'decimal3',
        critical: false,
        enabled: true,
        order: 5
      }
    ];
  };

  const fetchWellData = useCallback(async () => {
    if (!selectedWellId) return;
    setIsLoadingData(true);
    try {
      const wellDataPromise = apiFetch(`/api/wells/${selectedWellId}`);
      const plansPromise = apiFetch('/api/db/WellPlan');
      const reportsPromise = apiFetch(`/api/ddr?wellId=${selectedWellId}`);
      const surveyHeadersPromise = apiFetch(`/api/db/SurveyHeader?filter=WellID,eq,${selectedWellId}`);
      const attachmentsPromise = apiFetch('/api/attachments');
      const bhaPromise = apiFetch('/api/db/BHA');
      const bhaComponentsPromise = apiFetch('/api/db/BHA_Component');
      const casingRunsPromise = apiFetch(`/api/db/CasingRun?filter=WellID,eq,${selectedWellId}`).catch(() => []);

      const [wellData, plans, reports, surveyHeadersData, allAttachments, allBHA, allBHAComponents, casingRunsResponse] = await Promise.all([
        wellDataPromise,
        plansPromise,
        reportsPromise,
        surveyHeadersPromise,
        attachmentsPromise,
        bhaPromise,
        bhaComponentsPromise,
        casingRunsPromise
      ]);

      setWell(wellData);
      const foundPlan = Array.isArray(plans) ? plans.find(p => String(p.WellID) === String(selectedWellId)) : null;
      setPlan(foundPlan || null);
      setDailyReports(Array.isArray(reports) ? reports.sort((a, b) => new Date(b.ReportDate) - new Date(a.ReportDate)) : []);
      setSurveyHeaders(Array.isArray(surveyHeadersData) ? surveyHeadersData : []);
      setCasingRuns(Array.isArray(casingRunsResponse) ? casingRunsResponse : []);
      
      // Fetch related table data using proper foreign key relationships
      
      // 1. Fetch DrillingParameters for all reports of this well
      if (Array.isArray(reports) && reports.length > 0) {
        const reportIds = reports.map(report => report.ReportID).filter(Boolean);
        if (reportIds.length > 0) {
          try {
            const drillingParamsPromises = reportIds.map(reportId => 
              apiFetch(`/api/db/DrillingParameters?filter=ReportID,eq,${reportId}`)
            );
            const dailyWellDataPromises = reportIds.map(reportId => 
              apiFetch(`/api/db/DailyWellData?filter=ReportID,eq,${reportId}`)
            );
            
            const [allDrillingParamsArrays, allDailyWellDataArrays] = await Promise.all([
              Promise.all(drillingParamsPromises),
              Promise.all(dailyWellDataPromises)
            ]);
            
            const allDrillingParams = allDrillingParamsArrays.flat();
            const allDailyWellData = allDailyWellDataArrays.flat();
            
            setDrillingParameters(allDrillingParams);
            setDailyWellData(allDailyWellData);
          } catch (err) {
            console.warn('Failed to fetch drilling parameters or daily well data:', err);
            setDrillingParameters([]);
            setDailyWellData([]);
          }
        } else {
          setDrillingParameters([]);
          setDailyWellData([]);
        }
      } else {
        setDrillingParameters([]);
        setDailyWellData([]);
      }
      
      // 2. Fetch casing tally data for all casing runs
      if (Array.isArray(casingRunsResponse) && casingRunsResponse.length > 0) {
        const casingRunIds = casingRunsResponse.map(run => run.CasingRunID);
        const casingTallyPromises = casingRunIds.map(runId => 
          apiFetch(`/api/db/CasingTally?filter=CasingRunID,eq,${runId}`)
        );
        
        try {
          const allCasingTallyArrays = await Promise.all(casingTallyPromises);
          const allCasingTally = allCasingTallyArrays.flat();
          setCasingTally(allCasingTally);
        } catch (err) {
          console.warn('Failed to fetch casing tally data:', err);
          setCasingTally([]);
        }
      } else {
        setCasingTally([]);
      }
      
      // 3. Fetch all survey points for the well's survey headers
      if (Array.isArray(surveyHeadersData) && surveyHeadersData.length > 0) {
        const surveyPointsPromises = surveyHeadersData.map(header => 
          apiFetch(`/api/db/SurveyPoint?filter=SurveyID,eq,${header.SurveyID}`)
        );
        
        try {
          const allSurveyPointsArrays = await Promise.all(surveyPointsPromises);
          const allSurveyPoints = allSurveyPointsArrays.flat();
          setSurveyPoints(allSurveyPoints);
        } catch (err) {
          console.warn('Failed to fetch survey points:', err);
          setSurveyPoints([]);
        }
      } else {
        setSurveyPoints([]);
      }
      
      if (wellData && wellData.WellID) {
        const currentWellId = String(wellData.WellID);
        
        const filteredAttachments = Array.isArray(allAttachments) 
          ? allAttachments.filter(att => String(att.WellID) === currentWellId) 
          : [];
        setAttachments(filteredAttachments);

        const filteredBHA = Array.isArray(allBHA) 
          ? allBHA.filter(bha => String(bha.WellID) === currentWellId) 
          : [];
        setBhaData(filteredBHA);

        const bhaIds = filteredBHA.map(bha => bha.BHAID);
        const filteredBHAComponents = Array.isArray(allBHAComponents) 
          ? allBHAComponents.filter(comp => bhaIds.includes(comp.BHAID)) 
          : [];
        setBhaComponents(filteredBHAComponents);
      } else {
        setAttachments([]);
        setBhaData([]);
        setBhaComponents([]);
      }
      
      setError(null);
    } catch (err) {
      console.error("Failed to load well detail data:", err);
      setError(err.message || 'Failed to load well detail data');
      setWell(null);
      setPlan(null);
      setDailyReports([]);
      setSurveyHeaders([]);
      setSurveyPoints([]);
      setAttachments([]);
      setBhaData([]);
      setBhaComponents([]);
      setCasingRuns([]);
      setCasingTally([]);
      setDrillingParameters([]);
      setDailyWellData([]); // Added this line to clear dailyWellData on error
    }
    setIsLoadingData(false);
  }, [selectedWellId]);

  const calculateKpiValue = useCallback((kpiConfig) => {
    try {
      let sourceData = [];
      let rawValue = null;

      console.log(`Calculating KPI: ${kpiConfig.title}`, {
        format: kpiConfig.format,
        unit: kpiConfig.unit,
        tableName: kpiConfig.tableName,
        columnName: kpiConfig.columnName,
        calculationMethod: kpiConfig.calculationMethod
      });

      // Determine data source based on table name with proper relationships
      switch (kpiConfig.tableName) {
        case 'Well':
          if (well) {
            rawValue = well[kpiConfig.columnName];
          }
          break;
        case 'DailyWellData':
          // Use properly fetched daily well data joined by ReportID
          sourceData = dailyWellData;
          break;
        case 'DailyReport':
          sourceData = dailyReports;
          break;
        case 'DrillingParameters':
          // Use properly fetched drilling parameters joined by ReportID
          sourceData = drillingParameters;
          break;
        case 'SurveyPoint':
          sourceData = surveyPoints;
          break;
        case 'BHA':
          sourceData = bhaData;
          break;
        case 'BHA_Component':
          sourceData = bhaComponents;
          break;
        case 'CasingRun':
          sourceData = casingRuns;
          break;
        case 'CasingTally':
          sourceData = casingTally;
          break;
        default:
          console.warn(`Unknown table name: ${kpiConfig.tableName}`);
          return 'N/A';
      }

      // If we have raw value (from single record like Well), return it
      if (rawValue !== null && rawValue !== undefined) {
        const formattedValue = formatDisplayValue(rawValue, { 
          type: kpiConfig.format || 'decimal2', 
          unit: kpiConfig.unit 
        });
        console.log(`Raw value formatted: ${rawValue} -> ${formattedValue} (format: ${kpiConfig.format || 'decimal2'})`);
        return formattedValue;
      }

      // Extract values from array data
      const values = sourceData.map(item => {
        const value = item[kpiConfig.columnName];
        return value !== null && value !== undefined ? parseFloat(value) || value : null;
      }).filter(v => v !== null && v !== undefined);

      console.log(`Extracted values for ${kpiConfig.title}:`, values);

      if (values.length === 0) {
        console.log(`No values found for ${kpiConfig.title}`);
        return 'N/A';
      }

      // Apply calculation method
      let result;
      switch (kpiConfig.calculationMethod) {
        case 'sum':
          result = values.reduce((sum, val) => sum + (parseFloat(val) || 0), 0);
          break;
        case 'average':
          const numericValues = values.filter(v => !isNaN(parseFloat(v)));
          result = numericValues.length > 0 
            ? numericValues.reduce((sum, val) => sum + parseFloat(val), 0) / numericValues.length
            : 0;
          break;
        case 'max':
          result = Math.max(...values.filter(v => !isNaN(parseFloat(v))));
          break;
        case 'min':
          result = Math.min(...values.filter(v => !isNaN(parseFloat(v))));
          break;
        case 'count':
          result = values.length;
          break;
        case 'latest':
          result = values[0]; // Assuming data is sorted latest first
          break;
        case 'first':
          result = values[values.length - 1]; // Last item in sorted array
          break;
        default:
          result = values[0] || 0;
      }

      console.log(`Calculated result for ${kpiConfig.title}: ${result} (method: ${kpiConfig.calculationMethod})`);

      // Format the result using the configured format
      if (kpiConfig.calculationMethod === 'count') {
        // For count method, use integer formatting regardless of configured format
        const formattedResult = formatDisplayValue(result, { type: 'int' });
        console.log(`Count formatted: ${result} -> ${formattedResult}`);
        return formattedResult;
      }

      const formatType = kpiConfig.format || 'decimal2';
      const formattedResult = formatDisplayValue(result, { 
        type: formatType, 
        unit: kpiConfig.unit 
      });
      
      console.log(`Final formatted: ${result} -> ${formattedResult} (format: ${formatType}, unit: ${kpiConfig.unit})`);
      return formattedResult;

    } catch (error) {
      console.error(`Error calculating KPI ${kpiConfig.title}:`, error);
      return 'Error';
    }
  }, [well, dailyReports, surveyPoints, bhaData, bhaComponents, casingRuns, casingTally, drillingParameters, dailyWellData]);

  const calculateKpiValues = useCallback(() => {
    const values = {};
    kpiConfigurations.forEach(kpi => {
      values[kpi.id] = calculateKpiValue(kpi);
    });
    setKpiValues(values);
  }, [kpiConfigurations, calculateKpiValue]);

  const testFormatDisplayValue = () => {
    const testValue = 1234.5678;
    const testFormats = [
      { type: 'decimal2', unit: 'm' },
      { type: 'decimal3', unit: 'm/hr' },
      { type: 'currency', unit: 'USD' },
      { type: 'int', unit: 'days' },
      { type: 'comma', unit: '' },
      { type: 'float', unit: 'kg' }
    ];

    console.log('=== Format Display Value Tests ===');
    testFormats.forEach(({ type, unit }) => {
      const result = formatDisplayValue(testValue, { type, unit });
      console.log(`Format: ${type}, Unit: ${unit}, Input: ${testValue}, Output: ${result}`);
    });
    console.log('=== End Format Tests ===');
  };

  useEffect(() => {
    fetchConfigurations();
    loadKpiConfigurations();
    loadWells();
    // Run format tests on first load
    testFormatDisplayValue();
  }, [fetchConfigurations, loadKpiConfigurations, loadWells]);

  useEffect(() => {
    if (selectedWellId) {
      fetchWellData();
    }
  }, [fetchWellData, selectedWellId]);

  useEffect(() => {
    if (kpiConfigurations.length > 0 && !isLoadingData) {
      calculateKpiValues();
    }
  }, [kpiConfigurations, calculateKpiValues, isLoadingData]);

  const formatDateOnly = (val) => val ? new Date(val).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';

  // Render well selector dropdown
  const renderWellSelector = () => (
    <div className="well-selector-container">
      <div className="selector-group">
        <label htmlFor="well-select">Well:</label>
        <select
          id="well-select"
          value={selectedWellId}
          onChange={handleWellChange}
          className="form-select"
          disabled={isLoadingData}
        >
          <option value="">Select a well...</option>
          {wells.map(well => (
            <option key={well.WellID} value={well.WellID}>
              {well.WellName || well.Name || well.name || `Well ${well.WellID}`}
            </option>
          ))}
        </select>
      </div>
    </div>
  );

  // Calculate directional survey summary from SurveyHeader and SurveyPoint data
  const calculateDirectionalSurveyMetrics = () => {
    if (surveyHeaders.length === 0 && surveyPoints.length === 0) {
      return {
        stats: [
          { value: 0, label: 'Survey Headers' },
          { value: 0, label: 'Survey Points' },
          { value: 'N/A', label: 'Max Depth' }
        ],
        highlights: [
          { 
            icon: '📏', 
            label: 'Total Surveys', 
            value: '0 surveys' 
          },
          { 
            icon: '🎯', 
            label: 'Data Points', 
            value: '0 points' 
          },
          { 
            icon: '📐', 
            label: 'Coverage', 
            value: 'No data' 
          }
        ]
      };
    }

    const depths = surveyPoints.map(p => p.MD).filter(d => d !== null && d !== undefined);
    const inclinations = surveyPoints.map(p => p.Inclination).filter(i => i !== null && i !== undefined);
    const azimuths = surveyPoints.map(p => p.Azimuth).filter(a => a !== null && a !== undefined);
    const doglegValues = surveyPoints.map(p => p.DogLeg).filter(d => d !== null && d !== undefined);
    
    const maxDepth = depths.length > 0 ? Math.max(...depths) : 0;
    const maxInclination = inclinations.length > 0 ? Math.max(...inclinations) : 0;
    const avgInclination = inclinations.length > 0 ? inclinations.reduce((sum, inc) => sum + inc, 0) / inclinations.length : 0;
    const maxDogleg = doglegValues.length > 0 ? Math.max(...doglegValues) : 0;

    // Get latest survey header
    const latestSurvey = surveyHeaders.length > 0 ? 
      surveyHeaders.sort((a, b) => new Date(b.SurveyDate || b.CreatedAt) - new Date(a.SurveyDate || a.CreatedAt))[0] : null;
    
    return {
      stats: [
        { value: surveyHeaders.length, label: 'Survey Headers' },
        { value: surveyPoints.length, label: 'Survey Points' },
        { value: formatDisplayValue(maxDepth, { type: 'decimal2', unit: 'm' }), label: 'Max Depth' }
      ],
      highlights: [
        { 
          icon: '📏', 
          label: 'Measured Depth', 
          value: formatDisplayValue(maxDepth, { type: 'decimal2', unit: 'm' }) 
        },
        { 
          icon: '📐', 
          label: 'Max Inclination', 
          value: formatDisplayValue(maxInclination, { type: 'decimal2', unit: '°' }) 
        },
        { 
          icon: '🎯', 
          label: 'Latest Survey', 
          value: latestSurvey ? latestSurvey.SurveyName || `Survey ${latestSurvey.SurveyID}` : 'N/A'
        }
      ]
    };
  };

  // Calculate casing tally summary from CasingRun and CasingTally data
  const calculateCasingTallySummary = () => {
    if (casingTally.length === 0) {
      return {
        stats: [
          { value: 0, label: 'Total Joints' },
          { value: 0, label: 'Casing Runs' },
          { value: 'N/A', label: 'Total Length' }
        ],
        highlights: [
          { 
            icon: '📋', 
            label: 'Tally Joints', 
            value: '0 joints' 
          },
          { 
            icon: '📏', 
            label: 'Total Length', 
            value: '0.00 m' 
          },
          { 
            icon: '⚖️', 
            label: 'Total Weight', 
            value: '0.00 kg' 
          }
        ]
      };
    }

    const totalJoints = casingTally.length;
    const totalLength = casingTally.reduce((sum, joint) => sum + (parseFloat(joint.JointLength) || 0), 0);
    const totalWeight = casingTally.reduce((sum, joint) => sum + (parseFloat(joint.WeightPerFoot) * parseFloat(joint.JointLength) || 0), 0);
    const uniqueSizes = new Set(casingTally.map(joint => joint.OuterDiameter).filter(Boolean)).size;

    return {
      stats: [
        { value: totalJoints, label: 'Total Joints' },
        { value: casingRuns.length, label: 'Casing Runs' },
        { value: formatDisplayValue(totalLength, { type: 'decimal2', unit: 'm' }), label: 'Total Length' }
      ],
      highlights: [
        { 
          icon: '📋', 
          label: 'Tally Joints', 
          value: `${totalJoints} joints` 
        },
        { 
          icon: '📏', 
          label: 'Total Length', 
          value: formatDisplayValue(totalLength, { type: 'decimal2', unit: 'm' }) 
        },
        { 
          icon: '⚖️', 
          label: 'Total Weight', 
          value: formatDisplayValue(totalWeight, { type: 'decimal2', unit: 'kg' }) 
        }
      ]
    };
  };

  if (isLoadingData || isLoadingConfig) {
    return (
      <div className="well-detail-professional">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading well data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="well-detail-professional">
        <div className="error-container">
          <div className="error-icon">⚠️</div>
          <h3>Data Loading Error</h3>
          <p>{error}</p>
          <button className="retry-button" onClick={() => window.location.reload()}>
            Retry Loading
          </button>
        </div>
      </div>
    );
  }

  if (!well) {
    return (
      <div className="well-detail-professional">
        <div className="not-found-container">
          <div className="not-found-icon">🔍</div>
          <h3>Well Not Found</h3>
          <p>The requested well could not be located in the database.</p>
          <button className="back-button" onClick={() => navigate('/')}>
            Return to Well List
          </button>
        </div>
      </div>
    );
  }

  const directionalSurveyMetrics = calculateDirectionalSurveyMetrics();
  const casingTallySummary = calculateCasingTallySummary();

  return (
    <div className="well-detail-professional">
      {/* Professional Header */}
      <div className="professional-header">
        <div className="header-navigation">
          <button className="nav-back" onClick={() => navigate('/')}>
            ← Wells
          </button>
          <div className="header-breadcrumb">
            <span>Well Database</span>
            <span className="separator">/</span>
            <span className="current">{well?.WellName || 'Well Detail'}</span>
          </div>
        </div>

        {/* Well Selector and Information Layout */}
        <div className="well-info-layout">
          {/* Well Selector - Left Side */}
          <div className="well-selector-left">
            {renderWellSelector()}
          </div>

          {/* Well Information Grid - Right Side */}
          {well && (
            <div className="well-information-grid">
              <div className="well-details-grid">
                {wellTableColumns
                  .filter(col => col.visible !== false && !['WellID'].includes(col.name))
                  .slice(0, 12) // Show maximum 12 major columns
                  .map((column, index) => {
                    const value = well[column.name];
                    const displayValue = formatDisplayValue(value, { 
                      type: column.type?.toLowerCase()?.includes('date') ? 'date' : 
                            column.type?.toLowerCase()?.includes('number') || column.type?.toLowerCase()?.includes('decimal') ? 'decimal2' : 
                            'text',
                      unit: column.unit 
                    });
                    
                    return (
                      <div key={column.name} className="well-detail-item">
                        <span className="detail-label">{column.label || column.name}:</span>
                        <span className="detail-value">{displayValue || 'N/A'}</span>
                      </div>
                    );
                  })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Dynamic Key Performance Indicators */}
      <div className="kpi-section">
        <div className="kpi-section-header">
          <h2 className="section-title">Key Performance Indicators</h2>
          {(role === 'admin' || role === 'read-write') && (
            <button 
              className="configure-kpi-btn"
              onClick={() => navigate('/kpi-configuration')}
              title="Configure KPIs"
            >
              ⚙️ Configure KPIs
            </button>
          )}
        </div>
        <div className="kpi-grid">
          {kpiConfigurations.map((kpi) => (
            <KPICard 
              key={kpi.id}
              title={kpi.title}
              value={kpiValues[kpi.id] || 'Loading...'}
              unit={kpi.unit}
              icon={kpi.icon}
              subtitle={kpi.subtitle}
              critical={kpi.critical}
            />
          ))}
        </div>
      </div>

      {/* Operations Modules */}
      <div className="modules-section">
        <h2 className="section-title">Operations Modules</h2>
        <div className="modules-grid">
          <ModuleCard
            title="Daily Drilling Reports"
            icon="📊"
            description="Daily operations, costs, and drilling progress tracking"
            count={`${dailyReports.length} reports`}
            status={dailyReports.length > 0 ? "Active" : "Inactive"}
            onClick={() => navigate(`/ddr?wellId=${selectedWellId}`)}
          />
          
          <ModuleCard
            title="Bottom Hole Assembly"
            icon="🔧"
            description="BHA configurations and downhole tool assemblies"
            count={`${bhaData.length} configs`}
            status={bhaData.length > 0 ? "Active" : "Inactive"}
            onClick={() => navigate(`/bha-v2?wellId=${selectedWellId}`)}
          />
          
          <ModuleCard
            title="Directional Survey"
            icon="🧭"
            description="Wellbore trajectory and deviation survey data"
            count={`${surveyHeaders.length} surveys, ${surveyPoints.length} points`}
            status={surveyHeaders.length > 0 ? "Active" : "Inactive"}
            onClick={() => navigate(`/deviation-v2?wellId=${selectedWellId}`)}
          />
          
          <ModuleCard
            title="Casing Tally"
            icon="📋"
            description="Detailed casing joint tally and tubular inventory"
            count={`${casingTally.length} joints`}
            status={casingTally.length > 0 ? "Active" : "Inactive"}
            onClick={() => navigate(`/casing-v2?wellId=${selectedWellId}`)}
          />
          
          <ModuleCard
            title="Well Attachments"
            icon="📎"
            description="Technical documents, reports, and media files"
            count={`${attachments.length} files`}
            status={attachments.length > 0 ? "Active" : "Inactive"}
            onClick={() => navigate(`/attachments?wellId=${selectedWellId}`)}
          />
          
          <ModuleCard
            title="Well Analytics"
            icon="📈"
            description="Performance analysis and operational insights"
            count="Dashboard"
            status="Active"
            onClick={() => navigate(`/well-dashboard/${selectedWellId}`)}
          />
        </div>
      </div>

      </div>
    );
  }